'use client';

import {
  APP_REDIRECT_URI,
  SCOPELY_AUTH_SERVER,
  SCOPELY_GUEST_TENANT,
  SCOPELY_LOGIN_CLIENT_ID,
  SCOPELY_LOGIN_ENDPOINT,
  SCOPELY_LOGOUT_REDIRECT_URI,
  TITAN_API_KEY,
} from '@wwe-portal/web/utils/config';

import { getTrackerInstance } from '../analytics/titan';
import { addDDError } from '../analytics/datadog';
import type { ScopelyIdentity } from '@scopely/web-sdk-scopely-account';
import type { LoginIdentityOperation } from '@scopely/web-sdk-scopelyid';

/**
 * Scopely Account 2.0 (WHIP-66192).
 *
 * The sanctioned entry point stays `bootstrapScopelyIdentity`: the 2.0 package is
 * a compatibility facade over the same 1.0-shaped API, and Playgami's
 * `scopely_account_v2_enabled` flag — resolved over the network on every cold
 * call — decides which OAuth2 version it hands back. Nothing here selects a
 * version, by design.
 *
 * `@scopely/web-sdk-scopely-account` replaces `@scopely/web-sdk-identity`.
 * `@scopely/web-sdk-scopelyid` stays — the guide is explicit that it must not be
 * removed — but moves to >=0.22.0, since it still supplies `Config`,
 * `OktaProviderConfig` and the identity-operation enum for the 1.0 leg.
 */

const ref: { current: ScopelyIdentity | null } = {
  current: null,
};

/**
 * `LoginIdentityOperation.Login`, without a runtime import.
 *
 * The enum lives in `@scopely/web-sdk-scopelyid`, which we deliberately load
 * dynamically — importing the enum for its value alone would pull the whole SDK
 * into the initial bundle.
 */
export const LOGIN_OPERATION = 'login' as LoginIdentityOperation;

export const getScopelyId = async () => {
  if (ref.current == null) {
    if (!TITAN_API_KEY) throw new Error('TITAN_API_KEY is missing');

    const { Config, OktaProviderConfig } = await import(
      '@scopely/web-sdk-scopelyid'
    );
    const { bootstrapScopelyIdentity } = await import(
      '@scopely/web-sdk-scopely-account'
    );

    const { tracker } = getTrackerInstance();

    // Seed the Titan tenant before bootstrapping.
    //
    // `bootstrapScopelyIdentity` picks the OAuth2 version from
    // GET {authServer}/oauth/configs/{apiKey}?userId={tenant}&deviceToken={dt},
    // and Playgami's audience matches on that `userId`. The tenant is only ever
    // written by the app-open listener, from the logged-in portal user id — so at
    // the one moment it is read here it is '', and the SDK omits the parameter
    // (it is set only `if (opts.userId)`). The request arrives anonymous and
    // always resolves to 1.0.
    //
    // Seeding it here rather than in the listener is deliberate: that listener is
    // a `dynamic()` import, so a fast login click can beat it to the tenant and
    // the version would silently vary between sessions.
    //
    // Guarded so a real tenant is never overwritten — this only fills the
    // logged-out gap.
    if (!tracker.getContext().getTenant()) {
      tracker.getContext().setTenant(SCOPELY_GUEST_TENANT);
    }

    const oktaProviderConfig = new OktaProviderConfig(
      SCOPELY_LOGIN_ENDPOINT!,
      SCOPELY_LOGIN_CLIENT_ID!,
      APP_REDIRECT_URI!,
      SCOPELY_LOGOUT_REDIRECT_URI!
    );

    const scopelyIdConfig = new Config(oktaProviderConfig, SCOPELY_AUTH_SERVER);

    try {
      ref.current = await bootstrapScopelyIdentity({
        config: scopelyIdConfig,
        tracker: tracker as never,
      });
    } catch (error) {
      // Bootstrap resolves the OAuth2 version over the network
      // (GET {authServer}/oauth/configs/{TITAN_API_KEY}). A non-2xx throws
      // ResolverError with no cached fallback, which used to leave the login
      // button dead and silent. Report it and rethrow — `ref.current` stays
      // null, so the next attempt re-resolves rather than caching the failure.
      addDDError(error, { reason: 'scopely identity bootstrap failed' });
      throw error;
    }
  }
  return ref.current;
};

export type ScopelyOAuthError = {
  error: string;
  description?: string;
  /** The user backed out at the IdP — not a failure worth alarming on. */
  isUserCancelled: boolean;
  /** Transient on Scopely's side; retrying the same login can succeed. */
  isRetryable: boolean;
};

const RETRYABLE_OAUTH_ERRORS = ['server_error', 'temporarily_unavailable'];

/**
 * Read an OAuth error off a callback URI.
 *
 * The SDK will not do this for us: `AuthorizationUri.parse` returns undefined
 * whenever `code` is absent, so every documented error code collapses into a
 * generic "not an authorization uri" and we used to swallow all of them.
 *
 * Errors can arrive in the query string (2.0 uses `response_mode=query`) or in
 * the fragment (the 1.0 Okta leg), so both are checked.
 */
export const readOAuthCallbackError = (
  uri: string
): ScopelyOAuthError | null => {
  let url: URL;
  try {
    url = new URL(uri);
  } catch {
    return null;
  }

  const hashParams = new URLSearchParams(url.hash.replace(/^#/, ''));
  const error = url.searchParams.get('error') ?? hashParams.get('error');
  if (!error) return null;

  return {
    error,
    description:
      url.searchParams.get('error_description') ??
      hashParams.get('error_description') ??
      undefined,
    isUserCancelled: error === 'access_denied',
    isRetryable: RETRYABLE_OAUTH_ERRORS.includes(error),
  };
};

/**
 * Complete a Scopely logout redirect, if this URI is one.
 *
 * Returns false when the URI is not a logout callback. Note `isLogoutUri` is a
 * plain `startsWith(logoutRedirectUri)`, so while the logout redirect URI is the
 * login callback it matches login callbacks too — always test `isAuthorizeUri`
 * first.
 */
export const finishScopelyLogout = async (currentUri: string) => {
  const scopelyId = await getScopelyId();
  if (!scopelyId.isLogoutUri(currentUri)) return false;

  const result = await scopelyId.logoutResult(currentUri);
  if (!result.isSuccessful()) {
    addDDError(new Error('Scopely logout callback failed'), {
      reason: 'scopely logout failed',
      error: result.getError(),
    });
  }
  return true;
};
